import { useState } from "react";
import { X, Check, Moon, Zap, Leaf, Sun, Crown, Waves, Sprout, Flame, Flower2, CircleDot, Coffee } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const THEME_ICONS = {
  "midnight-blue": { Icon: Moon, colors: ["#3b82f6", "#1e40af"] },
  "electric-cyan": { Icon: Zap, colors: ["#22d3ee", "#06b6d4"] },
  "emerald-dark": { Icon: Leaf, colors: ["#10b981", "#047857"] },
  "amber-glow": { Icon: Sun, colors: ["#f59e0b", "#d97706"] },
  "royal-purple": { Icon: Crown, colors: ["#a855f7", "#7c3aed"] },
  "ocean-teal": { Icon: Waves, colors: ["#06b6d4", "#0e7490"] },
  "neon-lime": { Icon: Sprout, colors: ["#84cc16", "#4d7c0f"] },
  "light-coral": { Icon: Flame, colors: ["#f43f5e", "#fb7185"] },
  "light-fuchsia": { Icon: Flower2, colors: ["#c026d3", "#e879f9"] },
  "light-orange": { Icon: CircleDot, colors: ["#ea580c", "#fb923c"] },
  "light-beige": { Icon: Coffee, colors: ["#92400e", "#d4a574"] },
};

const PaletteIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Palette shape */}
    <path d="M24 4C12.95 4 4 12.95 4 24s8.95 20 20 20c2.76 0 5-2.24 5-5 0-1.22-.46-2.36-1.24-3.22-.74-.82-1.16-1.94-1.16-3.08C26.6 30.06 28.7 28 31.3 28H36c5.52 0 10-4.48 10-10C46 9.4 36.42 4 24 4z" fill="#D4A04A" stroke="#C08B30" strokeWidth="1.5"/>
    {/* Thumb hole */}
    <ellipse cx="34" cy="34" rx="3" ry="3.5" fill="#1a1d2e"/>
    {/* 11 color blobs for each theme */}
    <circle cx="13" cy="18" r="3.2" fill="#3b82f6"/>
    <circle cx="20" cy="12" r="3" fill="#22d3ee"/>
    <circle cx="28" cy="11" r="2.8" fill="#10b981"/>
    <circle cx="35" cy="14" r="2.8" fill="#f59e0b"/>
    <circle cx="39" cy="21" r="2.6" fill="#a855f7"/>
    <circle cx="12" cy="27" r="2.8" fill="#06b6d4"/>
    <circle cx="19" cy="22" r="2.6" fill="#84cc16"/>
    <circle cx="17" cy="34" r="2.8" fill="#f43f5e"/>
    <circle cx="24" cy="30" r="2.6" fill="#c026d3"/>
    <circle cx="24" cy="20" r="2.4" fill="#ea580c"/>
    <circle cx="30" cy="24" r="2.6" fill="#92400e"/>
  </svg>
);

const ThemePicker = () => {
  const { themeId, setThemeId, themes } = useTheme();
  const [open, setOpen] = useState(false);

  const darkThemes = ["midnight-blue", "electric-cyan", "emerald-dark", "amber-glow", "royal-purple", "ocean-teal", "neon-lime"];
  const lightThemes = ["light-coral", "light-fuchsia", "light-orange", "light-beige"];

  const renderThemeButton = (id) => {
    const t = themes[id];
    if (!t) return null;
    const isActive = themeId === id;
    const iconData = THEME_ICONS[id];
    const ThemeIcon = iconData?.Icon;

    return (
      <button
        key={id}
        onClick={() => setThemeId(id)}
        data-testid={`theme-option-${id}`}
        className="flex items-center gap-3 p-2 pr-4 rounded-xl transition-all hover:scale-[1.02]"
        style={{
          backgroundColor: isActive ? t.accent + "18" : "transparent",
          border: isActive ? `2px solid ${t.accent}` : "2px solid transparent",
        }}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${iconData?.colors[0]}, ${iconData?.colors[1]})`,
            boxShadow: isActive ? `0 0 12px ${iconData?.colors[0]}60` : "none",
          }}
        >
          {ThemeIcon && <ThemeIcon className="text-white" style={{ width: 15, height: 15 }} />}
        </div>
        <span className="text-xs font-semibold whitespace-nowrap" style={{ color: isActive ? t.accent : "var(--color-text-muted)" }}>
          {t.name}
        </span>
        {isActive && <Check className="w-3.5 h-3.5 ml-auto flex-shrink-0" style={{ color: t.accent }} />}
      </button>
    );
  };

  return (
    <div className="fixed bottom-6 left-6 z-50" data-testid="theme-picker">
      {open && (
        <div
          className="absolute bottom-16 left-0 p-4 rounded-2xl shadow-2xl w-[220px]"
          style={{
            backgroundColor: "var(--color-card)",
            border: "1px solid var(--color-border)",
            backdropFilter: "blur(16px)",
          }}
          data-testid="theme-picker-panel"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-black tracking-wider uppercase" style={{ color: "var(--color-text)" }}>
              Themes
            </span>
            <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:opacity-80" style={{ color: "var(--color-text-muted)" }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Dark Themes */}
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5 px-1" style={{ color: "var(--color-text-dim)" }}>Dark</p>
          <div className="flex flex-col gap-0.5 mb-3">
            {darkThemes.map(renderThemeButton)}
          </div>

          {/* Divider */}
          <div className="h-px mb-3" style={{ backgroundColor: "var(--color-border)" }}></div>

          {/* Light Themes */}
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5 px-1" style={{ color: "var(--color-text-dim)" }}>Light</p>
          <div className="flex flex-col gap-0.5">
            {lightThemes.map(renderThemeButton)}
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all hover:scale-110"
        style={{
          backgroundColor: "var(--color-bg)",
          border: "2px solid var(--color-border)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
        }}
        data-testid="theme-picker-button"
      >
        <PaletteIcon className="w-9 h-9" />
      </button>
    </div>
  );
};

export default ThemePicker;
